import {
  HostError,
  AppAuthFlowComplete,
  AppAuthFlowError,
  AppAuthFlowField,
  AppAuthFlowMethod,
  AppAuthFlowState,
  AppAuthFlowStep,
  AppAuthLoginRequest,
  AppAuthLoginResult,
  AppAuthLogoutResult,
  AppAuthStatus,
  authFlowActionUnavailableMessage,
  authFlowNotStartedMessage,
  widgetAuthStatus,
} from '@finxone-platform/shared/app-sdk';
import { NavigationOptionsType } from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import {
  HeadlessAuthField,
  HeadlessAuthFlowResult,
  HeadlessAuthStep,
  HeadlessAuthStepError,
} from '../../../../lib/services/headless-auth-flow/headless-auth-flow.models';
import { KeycloakWrapperService } from '../../../../lib/services/keycloak-wrapper-service/keycloak-wrapper.service';
import { NavigationService } from '../../../../lib/services/navigation-service/navigation.service';
import { SocketService } from '../../../../lib/services/socket-service/socket-service.service';
import { ProfileState } from '../../../../lib/state/user-profile.state';
import { logoutAndDisconnectWebSockets } from '../../components-library/logout/logout.utils';
import {
  ONBOARDING_LOGIN_REDIRECT_PATH,
  performAutoLoginRedirect,
} from '../../../../lib/utils/cta-button-actions/onboarding-zone/onboarding-actions.utils';

/**
 * The `auth.*` operations, plus the two session *mechanics* the onboarding bridge
 * borrows for `submitIndividual({ onSuccess })`.
 *
 * ## Why it is one bridge and not two helpers
 *
 * Signing a customer in and sending them to login are the same two actions
 * whether they were asked for by `sdk.auth.login()` or by the follow-up on a
 * submit. Written twice they drift — and the way they drift is a customer landing
 * on a different page depending on which surface they came through. So the
 * onboarding bridge takes `signInWithSession` / `goToLogin` from here rather than
 * calling Keycloak itself (see CustomWidgetOnboardingDeps.auth).
 *
 * ## What never reaches the widget
 *
 * - The access, refresh and id tokens. `auth.status()` answers a boolean, an id
 *   and a role; `signInWithSession` takes the tokens *from the host's own HTTP
 *   response* and puts them straight into the platform's storage. They are never
 *   a return value, never on a stream, never in an error message. A sandboxed
 *   frame can always navigate itself, so a token a bundle holds is a token a
 *   bundle can post to anyone.
 * - The Keycloak instance, realm, client id and the constructed auth URL. A
 *   widget asks the host to go to login; it does not get told where that is.
 *
 * ## Where the answer comes from
 *
 * `ProfileState` — the same slice the `profile` data topic serves. Deliberately
 * *not* `context.user`, whose `roles` is the role segment of the page URL (an
 * anonymous visitor on a `/customer/...` onboarding page has it too) and whose
 * `id` comes from a provider field the zone injector does not currently populate.
 * One source, so `auth.status()` and the `profile` topic cannot disagree.
 *
 * ## The managed login flow (`auth.flow.*`)
 *
 * The same bridge also serves the four managed-login operations, over
 * `HeadlessAuthFlowService`. They live here rather than in a bridge of their own
 * for the reason above: a flow that completes signs the customer in, and it must
 * be the *same* sign-in `signInWithSession` performs — one `performAutoLoginRedirect`
 * call, one landing page, one set of storage keys.
 *
 * The division of labour is the contract's (see the auth flow section of
 * contracts.ts): the service makes every HTTP call, holds the Keycloak cookie
 * jar and the opaque continuation URL, follows the final redirect and exchanges
 * the authorization code; this bridge only sanitises what the widget is allowed
 * to render and validates what it is allowed to send.
 */
export const CUSTOM_WIDGET_AUTH_OPERATIONS = [
  'auth.status',
  'auth.passwordPolicy',
  'auth.login',
  'auth.logout',
  'auth.flow.start',
  'auth.flow.submit',
  'auth.flow.selectMethod',
  'auth.flow.cancel',
] as const;

/**
 * `HeadlessAuthFlowService` as this bridge uses it. Structural rather than the
 * class, like every other collaborator here, so a spec hands in a plain object
 * and nothing drags the Angular injector into the bridge.
 */
export interface CustomWidgetAuthFlowService {
  start(options?: { loginHint?: string }): Promise<HeadlessAuthFlowResult>;
  submit(values: Record<string, string | string[]>, action?: string): Promise<HeadlessAuthFlowResult>;
  selectMethod(executionId: string): Promise<HeadlessAuthFlowResult>;
  cancel(): void;
  currentStep(): HeadlessAuthStep | null;
}

export interface CustomWidgetAuthDeps {
  store: Store;
  passwordPolicy?: () => Promise<{ policy: string }>;
  /**
   * Performs the real logout and the login redirect. Optional so a host that
   * grants no auth operation need not wire it; the operations then fail fast with
   * a clear message instead of pretending to have signed someone out.
   */
  keycloakWrapperService?: KeycloakWrapperService;
  /**
   * Disconnected before logout, exactly as the native logout widget does — an
   * open socket outliving the session is the bug `logoutAndDisconnectWebSockets`
   * exists to prevent. Optional for the same reason as above.
   */
  socketService?: SocketService;
  /** Resolves `redirectPageId` to a url, using the same builder `nav.goToPage` uses. */
  navigationService?: NavigationService;
  /**
   * Drives the managed login flow. Optional on the same terms as
   * `keycloakWrapperService`: the app builder canvas resolves neither, because
   * both would be real clients pointed at a real Keycloak. Absent, the
   * `auth.flow.*` operations fail fast with "not available on this host" rather
   * than being dropped from `handles()` — the SDK's support registry declares
   * them app-supported, so answering `unknown-operation` would tell a widget the
   * *platform* cannot do this when it is only this mount that cannot.
   */
  headlessAuthFlow?: CustomWidgetAuthFlowService;
  /**
   * Runs a navigation that ends this document. Defaults to a macrotask, and that
   * delay is load-bearing rather than cosmetic: every navigation here is committed
   * with `location.replace` / `location.href`, and doing it inside the operation's
   * own turn means the protocol response is never posted — the widget's promise
   * would hang and it could not paint "you're in" or "taking you to sign in".
   * Deferring by one task lets the response reach the frame first.
   *
   * Injectable so tests can run it synchronously and assert what was committed
   * without a real navigation.
   */
  schedule?: (task: () => void) => void;
}

export interface CustomWidgetAuthBridge {
  /** Whether `operation` belongs to this bridge. */
  handles(operation: string): boolean;
  call(operation: string, payload: Record<string, unknown>): Promise<unknown>;
  /** The current session, as a widget is allowed to see it. */
  status(): AppAuthStatus;
  /**
   * Consumes a session the host received on an onboarding response. Returns
   * whether it could: a response with no tokens is the platform's documented
   * best-effort failure, and the caller falls back to `goToLogin`.
   *
   * The tokens are a parameter and never a field on anything the widget sees.
   */
  signInWithSession(session: { accessToken?: string; refreshToken?: string }): boolean;
  /** Sends the customer to the project's sign-in flow. */
  goToLogin(redirectPageId?: string): void;
}

export function buildCustomWidgetAuthBridge(deps: CustomWidgetAuthDeps): CustomWidgetAuthBridge {
  const { store } = deps;
  const schedule = deps.schedule ?? ((task: () => void) => void setTimeout(task, 0));

  function status(): AppAuthStatus {
    const profile = store.selectSnapshot(ProfileState.getProfile);
    // The shared SDK rule, so the mock host and this bridge answer identically
    // for the same session — including on an onboarding zone, where there is no
    // profile at all and the honest answer is `{ authenticated: false }`.
    return widgetAuthStatus({ userId: profile?.id, activeRole: profile?.activeRole });
  }

  /**
   * The url to come back to after signing in. A named page resolves through the
   * platform's own navigation builder — the same call `nav.goToPage` makes, so a
   * widget cannot reach a page the host would not navigate to — and anything
   * unresolvable falls back to the destination the native onboarding CTA uses
   * rather than to a guess.
   */
  function loginRedirectUri(redirectPageId?: string): string {
    const origin = window.location.origin;
    if (redirectPageId && deps.navigationService) {
      const { pageUrl } = deps.navigationService.buildPageNavigationUrl({
        pageId: redirectPageId,
      } as NavigationOptionsType);
      if (pageUrl) return origin + (pageUrl.startsWith('/') ? pageUrl : `/${pageUrl}`);
    }
    return origin + ONBOARDING_LOGIN_REDIRECT_PATH;
  }

  function requireKeycloak(operation: string): KeycloakWrapperService {
    if (!deps.keycloakWrapperService) {
      throw new HostError('host-error', `${operation} is not available on this host`);
    }
    return deps.keycloakWrapperService;
  }

  function goToLogin(redirectPageId?: string): void {
    // Resolved now (it reads host state), committed a task later (see `schedule`).
    const keycloak = requireKeycloak('auth.login');
    const redirectUri = loginRedirectUri(redirectPageId);
    schedule(() => keycloak.login({ redirectUri }));
  }

  function signInWithSession(session: { accessToken?: string; refreshToken?: string }): boolean {
    if (!session?.accessToken || !session?.refreshToken) return false;
    const accessToken = session.accessToken;
    const refreshToken = session.refreshToken;
    // The native onboarding CTA's own helper: store where the Keycloak wrapper
    // reads them back, then hard-reload so the app re-initialises authenticated.
    // No returnUrl — a widget has no CTA outcome config, and /post-auth already
    // routes by the customer's state.
    schedule(() => performAutoLoginRedirect({ accessToken, refreshToken }));
    return true;
  }

  function requireFlow(operation: string): CustomWidgetAuthFlowService {
    if (!deps.headlessAuthFlow) {
      throw new HostError('host-error', `${operation} is not available on this host`);
    }
    return deps.headlessAuthFlow;
  }

  /**
   * The step the service is holding, sanitised. Both `submit` and `selectMethod`
   * validate against it rather than against anything the widget sent, which is
   * the whole point of holding it host-side.
   */
  function currentFlowStep(operation: string): AppAuthFlowStep {
    const step = requireFlow(operation).currentStep();
    // The shared message, so a widget that submits after a completion or a
    // cancel reads the same sentence here as it does against the mock host.
    if (!step) throw new HostError('host-error', authFlowNotStartedMessage(operation));
    return toWidgetAuthFlowStep(step);
  }

  async function runFlow(
    operation: string,
    call: () => Promise<HeadlessAuthFlowResult>,
  ): Promise<AppAuthFlowState> {
    let result: HeadlessAuthFlowResult;
    try {
      result = await call();
    } catch (error) {
      if (error instanceof HostError) throw error;
      throw new HostError('host-error', flowFailureMessage(operation, error));
    }
    // A wrong password is a *step* carrying `error`, not a rejection — the flow
    // is still alive and the widget re-renders it (see AppAuthFlowError).
    return result.kind === 'step' ? toWidgetAuthFlowStep(result.step) : completeFlow(result.tokens);
  }

  function completeFlow(tokens: {
    accessToken?: string;
    refreshToken?: string;
    idToken?: string;
  }): AppAuthFlowComplete {
    if (!tokens?.accessToken || !tokens?.refreshToken) {
      // Should not happen — the token endpoint answered and the id token
      // verified — but the customer is authenticated by this point, so throwing
      // would turn a successful login into a failed operation. Report the honest
      // `redirected: false` and let the widget offer sign-in instead.
      console.warn('auth.flow: the completed login returned no usable session; nothing was signed in.');
      return { status: 'complete', redirected: false };
    }
    const { accessToken, refreshToken, idToken } = tokens;
    // Deferred for the same reason as signInWithSession: this replaces the
    // document, and committing it inside the operation's own turn means the
    // widget never receives the `complete` response it paints its success state
    // from. The tokens are parameters here and on no result that crosses the port.
    schedule(() => performAutoLoginRedirect({ accessToken, refreshToken, idToken }));
    return { status: 'complete', redirected: true };
  }

  function logout(): AppAuthLogoutResult {
    const keycloak = requireKeycloak('auth.logout');
    schedule(() => {
      if (deps.socketService) {
        // The native path exactly: drop the socket, then the platform logout
        // (revoke server-side, clear storage, redirect).
        logoutAndDisconnectWebSockets(deps.socketService, keycloak);
      } else {
        keycloak.logout();
      }
    });
    // True means "begun": the redirect takes this document with it, so there is
    // nothing later to report from.
    return { signedOut: true };
  }

  return {
    handles: (operation) => (CUSTOM_WIDGET_AUTH_OPERATIONS as readonly string[]).includes(operation),
    call: async (operation, payload) => {
      switch (operation) {
        case 'auth.status':
          return status();
        case 'auth.passwordPolicy':
          if (!deps.passwordPolicy) {
            throw new HostError('host-error', 'auth.passwordPolicy is not available on this host');
          }
          return deps.passwordPolicy();
        case 'auth.login': {
          const request = payload as AppAuthLoginRequest;
          const redirectPageId =
            request?.redirectPageId === undefined ? undefined : String(request.redirectPageId);
          goToLogin(redirectPageId);
          return { navigated: true } satisfies AppAuthLoginResult;
        }
        case 'auth.logout':
          return logout();
        case 'auth.flow.start': {
          const flow = requireFlow('auth.flow.start');
          const loginHint = payload['loginHint'];
          if (loginHint !== undefined && typeof loginHint !== 'string') {
            throw new HostError('host-error', 'auth.flow.start: loginHint must be a string');
          }
          // A start discards whatever was in progress; the service restarts the
          // transaction itself, so nothing is cancelled here first.
          return runFlow('auth.flow.start', () =>
            flow.start(loginHint === undefined ? {} : { loginHint }),
          );
        }
        case 'auth.flow.submit': {
          const flow = requireFlow('auth.flow.submit');
          const step = currentFlowStep('auth.flow.submit');
          const rawAction = payload['action'];
          if (rawAction !== undefined && typeof rawAction !== 'string') {
            throw new HostError('host-error', 'auth.flow.submit: action must be a string');
          }
          const action = rawAction ? rawAction : undefined;
          // Only the names the *held* step advertises. The server answers
          // `action_not_available` for anything else; rejecting here says so in
          // the shared sentence instead of spending a continuation URL on it.
          if (action && !step.actions.includes(action)) {
            throw new HostError('host-error', authFlowActionUnavailableMessage('auth.flow.submit', action));
          }
          const values = readFlowValues(payload['values'], action === undefined);
          return runFlow('auth.flow.submit', () => flow.submit(values, action));
        }
        case 'auth.flow.selectMethod': {
          const flow = requireFlow('auth.flow.selectMethod');
          const step = currentFlowStep('auth.flow.selectMethod');
          const methodId = String(payload['methodId'] ?? '');
          // The server validates the execution id against the current selection
          // set; an id from an earlier step, or one a widget assembled, is not
          // honoured there and is not sent from here.
          if (!(step.methods ?? []).some((method) => method.id === methodId)) {
            throw new HostError(
              'host-error',
              `auth.flow.selectMethod: '${methodId}' is not one of the current step's methods. ` +
                'Pass a AppAuthFlowMethod.id back unchanged.',
            );
          }
          return runFlow('auth.flow.selectMethod', () => flow.selectMethod(methodId));
        }
        case 'auth.flow.cancel': {
          const flow = requireFlow('auth.flow.cancel');
          // False means there was nothing to cancel — cancelling twice is a
          // no-op that says so, not an error. Cancelled either way, so an
          // abandoned PKCE transaction cannot survive.
          const cancelled = flow.currentStep() !== null;
          flow.cancel();
          return { cancelled };
        }
        default:
          throw new HostError('unknown-operation', `unknown-operation: ${operation}`);
      }
    },
    status,
    signInWithSession,
    goToLogin,
  };
}

/**
 * The wire step as a widget is allowed to see it.
 *
 * **Rebuilt field by field, never spread.** `HeadlessAuthFlowService` already
 * strips the continuation URL before a step leaves it (`toPublicStep`), so this
 * is the second of two independent guarantees rather than the only one: an
 * `action`, a `protocol` block, a field's server-side `value`, or any key a
 * future realm starts sending cannot reach the port from here, because nothing
 * is copied that is not named below.
 *
 * What deliberately *does* pass through is `metadata` minus the lifted
 * `authenticationMethods` — the masked OTP destination, the `totp_setup`
 * enrolment payload (secret, otpAuthUri, algorithm, digits, period) and the
 * `totp` credentials list. The contract documents that exception: the widget is
 * what renders the QR code, and there is no enrolment without it.
 */
export function toWidgetAuthFlowStep(step: HeadlessAuthStep): AppAuthFlowStep {
  const { authenticationMethods, ...metadata } = step.metadata ?? {};
  const mapped: AppAuthFlowStep = {
    // The one status a step can have; the wire spells it with an underscore.
    status: 'interaction-required',
    type: step.type,
    fields: (step.fields ?? []).map(toWidgetAuthFlowField),
    // Names only, and only the ones actually offered: an unavailable alternative
    // is one the server would answer `action_not_available` for.
    actions: (step.actions ?? [])
      .filter((action) => action?.available !== false)
      .map((action) => String(action.name)),
    metadata,
  };
  const methods = toWidgetAuthFlowMethods(authenticationMethods);
  if (methods.length) mapped.methods = methods;
  if (step.error) mapped.error = toWidgetAuthFlowError(step.error);
  if (typeof step.expiresIn === 'number') mapped.expiresIn = step.expiresIn;
  return mapped;
}

/**
 * Absent optionals are absent, not `undefined`: the contract reads a missing
 * `minLength` as "unconstrained here", and a serialised `undefined` key is a
 * different thing to a widget that checks with `in`.
 */
function toWidgetAuthFlowField(field: HeadlessAuthField): AppAuthFlowField {
  const mapped: AppAuthFlowField = {
    name: field.name,
    type: field.type,
    required: field.required === true,
  };
  if (typeof field.autocomplete === 'string') mapped.autocomplete = field.autocomplete;
  if (typeof field.minLength === 'number') mapped.minLength = field.minLength;
  if (typeof field.maxLength === 'number') mapped.maxLength = field.maxLength;
  return mapped;
}

function toWidgetAuthFlowError(error: HeadlessAuthStepError): AppAuthFlowError {
  const mapped: AppAuthFlowError = { code: error.code, message: error.message };
  if (typeof error.field === 'string' && error.field) mapped.field = error.field;
  return mapped;
}

/**
 * The "try another way" choices, lifted out of `metadata.authenticationMethods`
 * onto the step's own `methods` — the contract's shape, and the one
 * `auth.flow.selectMethod` validates against.
 *
 * An entry with no id is dropped rather than rendered: it could not be selected,
 * and a choice that does nothing when tapped is worse than one that is absent.
 * The id key is read under the aliases Keycloak's headless extension is known to
 * use for it (`authenticationExecution` is the form field the service posts it
 * back as).
 */
function toWidgetAuthFlowMethods(raw: unknown): AppAuthFlowMethod[] {
  if (!Array.isArray(raw)) return [];
  const methods: AppAuthFlowMethod[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const candidate = entry as Record<string, unknown>;
    const id = [candidate['id'], candidate['authenticationExecution'], candidate['executionId']].find(
      (value): value is string => typeof value === 'string' && value.length > 0,
    );
    if (!id) continue;
    const label = [candidate['label'], candidate['displayName']].find(
      (value): value is string => typeof value === 'string' && value.length > 0,
    );
    methods.push({ id, label: label ?? humaniseMethodId(id) });
  }
  return methods;
}

/** Last-resort display text for a method the server labelled with nothing. */
function humaniseMethodId(id: string): string {
  const words = id.replace(/[-_]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : id;
}

/**
 * Coerces the submitted values, refusing anything the wire encoder cannot
 * represent. `values` is optional only when an `action` was given — asking for
 * `resend_code` carries no answer to send with it.
 */
function readFlowValues(raw: unknown, required: boolean): Record<string, string | string[]> {
  if (raw === undefined && !required) return {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new HostError(
      'host-error',
      'auth.flow.submit requires values: an object keyed by the step’s field names',
    );
  }
  const values: Record<string, string | string[]> = {};
  for (const [name, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'string') {
      values[name] = value;
    } else if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
      values[name] = [...(value as string[])];
    } else {
      throw new HostError(
        'host-error',
        `auth.flow.submit: values.${name} must be a string or an array of strings`,
      );
    }
  }
  return values;
}

/**
 * The service writes its failures to be shown — HTML instead of JSON, a
 * continuation URL outside the realm, a protocol mismatch, an expired
 * transaction — and its class comment is explicit that it never puts values,
 * tokens or continuation URLs in one. The raw error object never travels: only
 * its message, and a generic sentence when it has none.
 */
function flowFailureMessage(operation: string, error: unknown): string {
  const message = error instanceof Error ? error.message.trim() : '';
  return message || `${operation} could not be completed`;
}
